import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { certificatesKeys } from '@/features/certificates/queries';
import { workOrderKeys } from '@/features/work-orders/queries';
import {
  drydockCampaignsApi,
  type DrydockCampaignListFilters,
} from './service';
import type {
  CancelDrydockCampaignInput,
  CreateDrydockCampaignInput,
  UpdateDrydockCampaignInput,
} from './schema';

/**
 * Query key factory. Lists are parameterised by parent (company,
 * vessel) + filters so different scopes stay cached independently.
 *
 *   drydockCampaignsKeys.all                              every cached query
 *   drydockCampaignsKeys.detail(id)                       single campaign
 *   drydockCampaignsKeys.byCompany(id, filters?)          company list
 *   drydockCampaignsKeys.byVessel(id, filters?)           vessel list
 *   drydockCampaignsKeys.workOrders(id)                   linked WO list
 *   drydockCampaignsKeys.certificates(id)                 renewed certs list
 */
export const drydockCampaignsKeys = {
  all: ['drydock-campaigns'] as const,
  detail: (id: string) => [...drydockCampaignsKeys.all, 'detail', id] as const,
  byCompany: (
    companyId: string,
    filters: DrydockCampaignListFilters = {},
  ) =>
    [...drydockCampaignsKeys.all, 'by-company', companyId, filters] as const,
  byVessel: (
    vesselId: string,
    filters: DrydockCampaignListFilters = {},
  ) =>
    [...drydockCampaignsKeys.all, 'by-vessel', vesselId, filters] as const,
  workOrders: (id: string) =>
    [...drydockCampaignsKeys.all, 'work-orders', id] as const,
  certificates: (id: string) =>
    [...drydockCampaignsKeys.all, 'certificates', id] as const,
};

export function useDrydockCampaign(id: string | undefined) {
  return useQuery({
    queryKey: id ? drydockCampaignsKeys.detail(id) : drydockCampaignsKeys.all,
    queryFn: () => drydockCampaignsApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useDrydockCampaignsByCompany(
  companyId: string | undefined,
  filters: DrydockCampaignListFilters = {},
) {
  return useQuery({
    queryKey: companyId
      ? drydockCampaignsKeys.byCompany(companyId, filters)
      : drydockCampaignsKeys.all,
    queryFn: () =>
      drydockCampaignsApi.listByCompany(companyId as string, filters),
    enabled: Boolean(companyId),
  });
}

export function useDrydockCampaignsByVessel(
  vesselId: string | undefined,
  filters: DrydockCampaignListFilters = {},
) {
  return useQuery({
    queryKey: vesselId
      ? drydockCampaignsKeys.byVessel(vesselId, filters)
      : drydockCampaignsKeys.all,
    queryFn: () =>
      drydockCampaignsApi.listByVessel(vesselId as string, filters),
    enabled: Boolean(vesselId),
  });
}

export function useDrydockCampaignWorkOrders(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? drydockCampaignsKeys.workOrders(id)
      : drydockCampaignsKeys.all,
    queryFn: () => drydockCampaignsApi.listWorkOrders(id as string),
    enabled: Boolean(id),
  });
}

export function useDrydockCampaignCertificates(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? drydockCampaignsKeys.certificates(id)
      : drydockCampaignsKeys.all,
    queryFn: () => drydockCampaignsApi.listCertificates(id as string),
    enabled: Boolean(id),
  });
}

/**
 * After a write, invalidate the entire campaigns subtree so list
 * pages refetch fresh data. Lifecycle transitions (start/complete/
 * cancel) and edits also touch any cached WO/certificate sub-list
 * for the same id, hence the targeted invalidation in those hooks.
 *
 * The cross-feature invalidations (workOrderKeys.all,
 * certificatesKeys.all) are scoped wide on purpose: a status flip
 * can affect badges shown in those modules' lists too (e.g. "linked
 * to in-progress drydock"), and the cost of an extra refetch is
 * trivial vs. the risk of stale UI.
 */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: drydockCampaignsKeys.all });
}

function invalidateAllAndCrossModules(
  qc: ReturnType<typeof useQueryClient>,
) {
  qc.invalidateQueries({ queryKey: drydockCampaignsKeys.all });
  qc.invalidateQueries({ queryKey: workOrderKeys.all });
  qc.invalidateQueries({ queryKey: certificatesKeys.all });
}

export function useCreateDrydockCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDrydockCampaignInput) =>
      drydockCampaignsApi.create(input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateDrydockCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDrydockCampaignInput) =>
      drydockCampaignsApi.update(id, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useStartDrydockCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => drydockCampaignsApi.start(id),
    onSuccess: () => invalidateAllAndCrossModules(qc),
  });
}

export function useCompleteDrydockCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => drydockCampaignsApi.complete(id),
    onSuccess: () => invalidateAllAndCrossModules(qc),
  });
}

export function useCancelDrydockCampaign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CancelDrydockCampaignInput) =>
      drydockCampaignsApi.cancel(id, input),
    onSuccess: () => invalidateAllAndCrossModules(qc),
  });
}

export function useDeleteDrydockCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => drydockCampaignsApi.remove(id),
    onSuccess: () => invalidateAll(qc),
  });
}
